/**
 * Calls the Zeroth API at /api on the portal's own origin. The portal's CloudFront distribution
 * forwards /api/* to the same API as app.zerothhq.com, authenticated with the shared Clerk session.
 */
export type TokenGetter = () => Promise<string | null>;

/** A failed API call, with the HTTP status so pages can tell "not allowed" from "broken". */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

export async function apiRequest<T>(
  getToken: TokenGetter,
  method: 'GET' | 'POST' | 'PATCH',
  path: string,
  body?: unknown
): Promise<T> {
  const token = await getToken();
  const response = await fetch(path, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const json = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
  if (!response.ok)
    throw new ApiError(json?.error?.message ?? `Request failed (${response.status}).`, response.status);
  return json as T;
}

export const errorMessage = (error: unknown) =>
  error instanceof Error && error.message ? error.message : 'Something went wrong.';
